import React, { useState } from 'react';
import { 
  FaBullhorn, 
  FaEnvelope, 
  FaBolt, 
  FaRocket, 
  FaCalendarAlt,
  FaUsers,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaPaperPlane,
  FaBell,
  FaSms,
  FaFilter,
  FaEye,
  FaEdit,
  FaTrash,
  FaArrowUp
} from 'react-icons/fa';

const MarketingManagement = () => {
    const [loading, setLoading] = useState(false);
    const [campaign, setCampaign] = useState({ 
        title: '', 
        message: '', 
        type: 'notification',
        audience: 'all',
        schedule: 'immediate'
    });
    const [message, setMessage] = useState({ text: '', type: '', show: false });
    const [activeTab, setActiveTab] = useState('create');
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    const showMessage = (text, type) => {
        setMessage({ text, type, show: true });
        setTimeout(() => {
            setMessage({ text: '', type: '', show: false });
        }, 3000);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        
        // Simulate API call
        setTimeout(() => {
            setLoading(false);
            showMessage(`Campaign "${campaign.title}" scheduled successfully!`, 'success');
            setCampaign({ 
                title: '', 
                message: '', 
                type: 'notification',
                audience: 'all',
                schedule: 'immediate'
            });
            setActiveTab('campaigns');
        }, 1500);
    };

    const campaignTypes = [
        { id: 'notification', label: 'Push Notification', icon: <FaBell />, color: 'blue' },
        { id: 'email', label: 'Email Campaign', icon: <FaEnvelope />, color: 'green' },
        { id: 'sms', label: 'SMS Blast', icon: <FaSms />, color: 'purple' },
        { id: 'inapp', label: 'In-App Message', icon: <FaBullhorn />, color: 'orange' }
    ];

    const audienceOptions = [
        { id: 'all', label: 'All Users', count: '2.5k' },
        { id: 'premium', label: 'Premium Users', count: '420' },
        { id: 'inactive', label: 'Inactive Users', count: '850' },
        { id: 'new', label: 'New Users (Last 7 days)', count: '180' }
    ];

    const recentCampaigns = [
        { 
            id: 1, 
            title: 'Welcome Series', 
            type: 'email', 
            icon: <FaEnvelope />,
            sentTo: 150, 
            date: 'Yesterday',
            status: 'completed',
            color: 'green',
            metrics: { openRate: '42%', clickRate: '18%' }
        },
        { 
            id: 2, 
            title: 'Weekend Match Boost', 
            type: 'notification', 
            icon: <FaBell />,
            sentTo: 1200, 
            date: '2 days ago',
            status: 'completed',
            color: 'blue',
            metrics: { openRate: '68%', clickRate: '32%' }
        },
        { 
            id: 3, 
            title: 'Festival Special Offer', 
            type: 'sms', 
            icon: <FaSms />,
            sentTo: 850, 
            date: '1 week ago',
            status: 'completed',
            color: 'purple',
            metrics: { openRate: '91%', clickRate: '45%' }
        },
        { 
            id: 4, 
            title: 'Profile Completion Reminder', 
            type: 'inapp', 
            icon: <FaBullhorn />,
            sentTo: 420, 
            date: 'In progress',
            status: 'scheduled',
            color: 'yellow',
            metrics: { openRate: '—', clickRate: '—' }
        }
    ];

    return (
        <div className="space-y-8">
            {/* Message Popup */}
            {message.show && (
                <div className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ${
                    message.type === 'success' ? 'bg-blue-500' : 'bg-gray-400'
                } text-white px-6 py-3 rounded-xl shadow-2xl flex items-center space-x-3 animate-slide-in`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        message.type === 'success' ? 'bg-blue-600' : 'bg-gray-500'
                    }`}>
                        {message.type === 'success' ? '✓' : '✗'}
                    </div>
                    <span className="font-medium">{message.text}</span>
                    <button onClick={() => setMessage({ ...message, show: false })} className="ml-4 hover:opacity-80">
                        <FaTrash className="text-sm" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                            <FaRocket className="mr-3 text-blue-500" />
                            Marketing & Campaigns
                        </h1>
                        <p className="text-gray-600 mt-1">Create and manage marketing campaigns</p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200 flex items-center">
                            <FaChartLine className="mr-2" /> Analytics
                        </button>
                        <button className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200 flex items-center">
                            <FaCalendarAlt className="mr-2" /> Schedule
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Campaigns</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">24</p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <FaBullhorn className="text-blue-500 text-xl" />
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-sm text-green-600">
                        <FaChartLine className="mr-2" /> 12% increase
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Active Campaigns</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">3</p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                            <FaBolt className="text-green-500 text-xl" />
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-sm text-gray-500">
                        <FaClock className="mr-2" /> Real-time
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Reach</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">2.5K</p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                            <FaUsers className="text-purple-500 text-xl" />
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-sm text-gray-500">
                        <FaUsers className="mr-2" /> Users reached
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Avg. Open Rate</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">64%</p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                            <FaChartLine className="text-orange-500 text-xl" />
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-sm text-green-600">
                        <FaArrowUp className="mr-2" /> 8% increase
                    </div>
                </div>
            </div>

            {/* Main Content Tabs */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-8">
                        <button
                            onClick={() => setActiveTab('create')}
                            className={`py-5 font-medium text-sm border-b-2 transition-colors ${
                                activeTab === 'create'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <FaRocket className="inline mr-2" /> Create Campaign
                        </button>
                        <button
                            onClick={() => setActiveTab('campaigns')}
                            className={`py-5 font-medium text-sm border-b-2 transition-colors ${
                                activeTab === 'campaigns'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <FaBullhorn className="inline mr-2" /> Recent Campaigns
                        </button>
                       
                    </nav>
                </div>

                <div className="p-8">
                    {activeTab === 'create' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Campaign Form */}
                            <div className="lg:col-span-2">
                                <div className="bg-gray-50 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-gray-800 mb-6">Create New Campaign</h3>
                                    
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Campaign Title</label>
                                            <input
                                                type="text"
                                                required
                                                value={campaign.title}
                                                onChange={(e) => setCampaign({ ...campaign, title: e.target.value })}
                                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="e.g., Diwali Special 50% Off"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Message Content</label>
                                            <textarea
                                                required
                                                rows="5"
                                                value={campaign.message}
                                                onChange={(e) => setCampaign({ ...campaign, message: e.target.value })}
                                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Enter your campaign message here..."
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Campaign Type</label>
                                                <div className="space-y-2">
                                                    {campaignTypes.map(type => (
                                                        <div 
                                                            key={type.id}
                                                            onClick={() => setCampaign({ ...campaign, type: type.id })}
                                                            className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all ${
                                                                campaign.type === type.id 
                                                                    ? `border-${type.color}-500 bg-${type.color}-50` 
                                                                    : 'border-gray-200 hover:border-gray-300'
                                                            }`}
                                                        >
                                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${
                                                                campaign.type === type.id 
                                                                    ? `bg-${type.color}-100 text-${type.color}-600` 
                                                                    : 'bg-gray-100 text-gray-400'
                                                            }`}>
                                                                {type.icon}
                                                            </div>
                                                            <span className="font-medium text-gray-700">{type.label}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Target Audience</label>
                                                <div className="space-y-2">
                                                    {audienceOptions.map(audience => (
                                                        <div 
                                                            key={audience.id}
                                                            onClick={() => setCampaign({ ...campaign, audience: audience.id })}
                                                            className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-all ${
                                                                campaign.audience === audience.id 
                                                                    ? 'border-blue-500 bg-blue-50' 
                                                                    : 'border-gray-200 hover:border-gray-300'
                                                            }`}
                                                        >
                                                            <div>
                                                                <p className="font-medium text-gray-700">{audience.label}</p>
                                                                <p className="text-sm text-gray-500">{audience.count} users</p>
                                                            </div>
                                                            {campaign.audience === audience.id && (
                                                                <FaCheckCircle className="text-blue-500" />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Schedule</label>
                                            <div className="flex gap-3">
                                                {['immediate', 'scheduled'].map(option => (
                                                    <button
                                                        key={option}
                                                        type="button"
                                                        onClick={() => setCampaign({ ...campaign, schedule: option })}
                                                        className={`px-4 py-3 rounded-xl font-medium transition-colors flex-1 ${
                                                            campaign.schedule === option 
                                                                ? 'bg-blue-500 text-white' 
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                    >
                                                        {option === 'immediate' ? 'Send Now' : 'Schedule Later'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <button
    type="submit"
    disabled={loading}
    className="mx-auto block w-full max-w-xs py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
>
    {loading ? (
        <div className="flex items-center">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
            Scheduling Campaign...
        </div>
    ) : (
        <>
            <FaPaperPlane className="mr-2" /> Launch Campaign
        </>
    )}
</button>

                                    </form>
                                </div>
                            </div>

                            {/* Preview Panel */}
                            <div>
                                <div className="bg-gray-50 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-gray-800 mb-6">Campaign Preview</h3>
                                    
                                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                                        <div className="flex items-center mb-4">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${
                                                campaignTypes.find(t => t.id === campaign.type)?.color || 'blue'
                                            }-100 text-${campaignTypes.find(t => t.id === campaign.type)?.color || 'blue'}-600`}>
                                                {campaignTypes.find(t => t.id === campaign.type)?.icon || <FaBell />}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800">{campaign.title || 'Campaign Title'}</p>
                                                <p className="text-sm text-gray-500">
                                                    {campaignTypes.find(t => t.id === campaign.type)?.label || 'Push Notification'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                            <p className="text-gray-700">
                                                {campaign.message || 'Your campaign message will appear here...'}
                                            </p>
                                        </div>
                                        
                                        <div className="text-sm text-gray-500">
                                            <div className="flex items-center justify-between mb-2">
                                                <span>Target Audience:</span>
                                                <span className="font-medium text-gray-700">
                                                    {audienceOptions.find(a => a.id === campaign.audience)?.label || 'All Users'}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span>Schedule:</span>
                                                <span className="font-medium text-gray-700">
                                                    {campaign.schedule === 'immediate' ? 'Send Immediately' : 'Scheduled'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'campaigns' && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-bold text-gray-800">Recent Campaigns</h3>
                                <button className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200 flex items-center">
                                    <FaFilter className="mr-2" /> Filter
                                </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {recentCampaigns.map(campaign => (
                                    <div 
                                        key={campaign.id} 
                                        className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-shadow duration-300"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center">
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
                                                    campaign.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                                                    campaign.color === 'green' ? 'bg-green-100 text-green-600' :
                                                    campaign.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                                                    'bg-yellow-100 text-yellow-600'
                                                }`}>
                                                    {campaign.icon}
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-gray-800">{campaign.title}</h4>
                                                    <p className="text-sm text-gray-500 flex items-center">
                                                        <FaUsers className="mr-1" /> {campaign.sentTo} users • {campaign.date}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                campaign.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {campaign.status === 'completed' ? 'Completed' : 'Scheduled'}
                                            </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div className="bg-gray-50 rounded-xl p-3">
                                                <p className="text-xs text-gray-500">Open Rate</p>
                                                <p className="text-lg font-bold text-gray-800">{campaign.metrics.openRate}</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-xl p-3">
                                                <p className="text-xs text-gray-500">Click Rate</p>
                                                <p className="text-lg font-bold text-gray-800">{campaign.metrics.clickRate}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex justify-end space-x-2 pt-4 border-t border-gray-100">
                                            <button className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors">
                                                <FaEye />
                                            </button>
                                            <button className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors">
                                                <FaEdit />
                                            </button>
                                            <button className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors">
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarketingManagement;